import React, { useState } from "react";
import { useUser } from "../contexts/UserContext";
import { PageHeader } from "./ui/PageHeader";
import { Button } from "./ui/Button";
import { Alert } from "./ui/Alert";

export default function Settings() {
  const { user } = useUser();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState(""); // success or error

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (!currentPassword || !newPassword || !confirmPassword) {
      setMessage("全ての項目を入力してください");
      setMessageType("error");
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage("新しいパスワードが一致しません");
      setMessageType("error");
      return;
    }

    if (newPassword.length < 6) {
      setMessage("パスワードは6文字以上で入力してください");
      setMessageType("error");
      return;
    }

    setIsLoading(true);
    setMessage("");

    try {
      const response = await fetch("/api/auth/change-password/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: user.email,
          current_password: currentPassword,
          new_password: newPassword,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage("パスワードが正常に変更されました");
        setMessageType("success");
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
      } else {
        setMessage(data.error || "パスワード変更に失敗しました");
        setMessageType("error");
      }
    } catch (error) {
      setMessage("ネットワークエラーが発生しました");
      setMessageType("error");
      console.error("Password change error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const inputClass =
    "w-full pl-11 pr-4 py-3 border border-prodia-border rounded-xl focus:border-prodia-primary focus:ring-2 focus:ring-prodia-primary/30 outline-none transition-all text-prodia-text-primary placeholder:text-prodia-text-muted bg-prodia-background focus:bg-prodia-surface text-sm";

  return (
    <div className="flex flex-col h-full bg-prodia-background">
      <PageHeader
        title="設定"
        description="アカウント設定とセキュリティ"
      />

      <div className="flex-1 overflow-auto px-4 sm:px-6 py-5">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div className="bg-prodia-surface rounded-xl p-6 border border-prodia-border shadow-prodia">
            <div className="text-center">
              <div className="w-16 h-16 bg-prodia-primary-subtle border border-prodia-border rounded-xl mx-auto mb-4 flex items-center justify-center">
                <i className="fas fa-user text-prodia-primary text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-prodia-text-primary mb-1">{user?.name}</h3>
              <p className="text-prodia-text-secondary text-sm">{user?.email}</p>
              <div className="mt-5 space-y-2">
                <div className="flex items-center justify-center gap-2 text-sm text-prodia-text-secondary">
                  <i className="fas fa-shield-alt text-success"></i>
                  <span>認証済みユーザー</span>
                </div>
                <div className="flex items-center justify-center gap-2 text-sm text-prodia-text-secondary">
                  <i className="fas fa-clock text-info"></i>
                  <span>アクティブ</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="bg-prodia-surface rounded-xl p-6 border border-prodia-border shadow-prodia">
            <div className="flex items-center gap-3 mb-6">
              <i className="fas fa-key text-prodia-primary text-lg"></i>
              <h2 className="text-lg font-semibold text-prodia-text-primary">パスワード変更</h2>
            </div>

            {message && (
              <div className="mb-6">
                <Alert variant={messageType === "success" ? "success" : "danger"}>{message}</Alert>
              </div>
            )}

            <form onSubmit={handlePasswordChange} className="space-y-5">
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-prodia-text-secondary">現在のパスワード</label>
                <div className="relative">
                  <i className="fas fa-lock absolute left-4 top-1/2 transform -translate-y-1/2 text-prodia-text-muted"></i>
                  <input
                    type="password"
                    placeholder="現在のパスワードを入力"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-prodia-text-secondary">新しいパスワード</label>
                <div className="relative">
                  <i className="fas fa-key absolute left-4 top-1/2 transform -translate-y-1/2 text-prodia-text-muted"></i>
                  <input
                    type="password"
                    placeholder="新しいパスワードを入力（6文字以上）"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-prodia-text-secondary">パスワード確認</label>
                <div className="relative">
                  <i className="fas fa-check-circle absolute left-4 top-1/2 transform -translate-y-1/2 text-prodia-text-muted"></i>
                  <input
                    type="password"
                    placeholder="新しいパスワードを再入力"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                size="lg"
                className="w-full"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>変更中...</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-save"></i>
                    <span>パスワードを変更</span>
                  </>
                )}
              </Button>
            </form>

            <Alert variant="info" title="パスワード変更に関して" className="mt-6">
              <ul className="space-y-1 text-xs list-none">
                <li>• パスワードは6文字以上で設定してください</li>
                <li>• 定期的なパスワード変更を推奨します</li>
                <li>• 他のサービスと同じパスワードは使用しないでください</li>
              </ul>
            </Alert>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
